import { ToggleButtonGroup } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import styled, { css } from 'styled-components';

import Tooltip from 'components/@efz/Tooltip';

import { themeBase } from 'constants/styles/global';

const colors = themeBase.color;

const fontWeightMedium = 500;

interface StyledSidebarProps {
    $open?: boolean;
    $headerHeight?: number;
}

export const StyledSidebar = styled(Drawer).attrs<StyledSidebarProps>((props) => ({
    $open: props?.$open ?? false,
    $headerHeight: props?.$headerHeight ?? 0,
}))<StyledSidebarProps>`
    z-index: 800 !important;
    position: relative !important;
    font-family: 'Poppins';

    .MuiPaper-root {
        top: ${({ $headerHeight }) => `${$headerHeight}px`} !important;
        overflow: inherit;
        width: ${({ $open }) => ($open ? '15.75rem' : '3.875rem')};
        background-color: ${colors.neutral50};
        border-right: 1px solid ${colors.steel100};
    }

    > .MuiPaper-root {
        height: ${({ $headerHeight }) => `calc(100vh - ${$headerHeight}px)`};
    }

    .MuiBackdrop-root {
        background-color: unset !important;
    }
`;

export const StyledSidebarContent = styled.div<{ $open: boolean }>`
    flex: 1;
    height: 100%;
    padding: 1rem;
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    align-items: ${({ $open }) => ($open ? 'stretch' : 'center')};
    row-gap: 1rem;

    .sidebar-content-top {
        flex: 1;
        display: flex;
        flex-flow: column nowrap;
        justify-content: flex-start;
        align-items: ${({ $open }) => ($open ? 'stretch' : 'center')};
        row-gap: 1rem;

        .sidebar-divider {
            width: 100%;
            height: 1px;
            background-color: ${colors.steel100};
            margin: 0.5rem 0;
        }
    }

    .icon-and-handler {
        display: flex;
        flex-flow: ${({ $open }) => `${$open ? 'row' : 'column'} nowrap`};
        justify-content: space-between;
        align-items: center;
    }

    img {
        display: ${({ $open }) => ($open ? 'block' : 'none')};
        max-width: ${({ $open }) => ($open ? '' : '32px')};
        max-height: ${({ $open }) => ($open ? '32px' : '')};
    }

    .sales-segment-wrapped {
        font-size: 0.6875rem;
        font-weight: ${fontWeightMedium};
        line-height: 1.125rem;
        color: ${colors.steel700};
    }

    .section-collapsed {
        all: unset;
        width: 2.3125rem;
        height: 2.3125rem;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 0.5rem;
        transition: 100ms background-color ease-in-out;

        svg {
            width: 1.25rem;
            height: 1.25rem;
        }

        &:hover,
        &.active {
            background-color: ${colors.steel50};
        }
        &:focus-visible {
            border: 1px solid ${colors.steel400};
        }
    }

    .clickable {
        max-height: 2.3125rem;
        cursor: ${({ $open }) => !$open && 'pointer'};
    }

    .avatar-icon {
        circle {
            fill: ${themeBase.white};
            stroke: ${colors.steel100};
        }
    }

    .powered-logo {
        display: ${({ $open }) => ($open ? 'block' : 'none')};
        margin-top: 0.5rem;
    }
`;

export const StyledSidebarSegmentButtonGroup = styled(ToggleButtonGroup)`
    max-height: 2rem;

    .MuiToggleButton-root {
        width: 50%;
        background-color: ${themeBase.white};
        max-height: 2rem;
        border: 1px solid ${colors.steel100};
        font-family: 'Poppins', sans-serif;
        font-size: 0.6875rem;
        font-weight: 500;

        &.Mui-selected {
            background-color: ${colors.steel100};
            box-shadow: 0px 4px 4px 0px #00000014 inset;
            color: ${colors.steel700};
        }
    }
`;

export const accordionStyles = css`
    top: 0 !important;
    width: 100%;
    border: none;
    box-shadow: none;

    .MuiAccordionSummary-root {
        min-height: unset;
    }
`;

export const StyledSidebarSection = styled.div<{ $open: boolean; $expanded: boolean }>`
    flex-direction: column;
    align-items: stretch;
    // show scrollbar if height surpasses
    // the container's max height
    ${({ $expanded }) =>
        $expanded &&
        `
            height: 2.5rem;
            overflow-y: auto;
            flex: 1 1 auto;
        `}

    .section-accordion {
        ${accordionStyles};

        &-summary {
            height: 40px;
            flex: 1;
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: stretch;
            font-size: 0.875rem;
            font-weight: ${fontWeightMedium};
            cursor: ${({ $open }) => $open && 'pointer'};
            padding: ${({ $open }) => $open && '0.5rem'};
            border-radius: 0.5rem;
            transition: 250ms background-color ease;

            &.active,
            &:hover {
                background-color: ${colors.steel50};
            }

            .Mui-expanded {
                margin: 0;
            }

            .MuiAccordionSummary-expandIconWrapper {
                transform: unset;
                transition: unset;

                svg {
                    transform: ${({ $expanded }) => `rotate(${$expanded ? 180 : 0}deg)`};
                    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
                }
            }

            div {
                display: flex;
                align-items: center;
                column-gap: 0.5rem;
            }
        }

        &-content {
            margin-top: 0rem;
            padding: 0rem 1rem 0.25rem 1rem;

            .menu {
                flex: 1;
                display: flex;
                flex-flow: column nowrap;
                justify-content: flex-start;
                align-items: stretch;
                row-gap: 0.5rem;
                margin-top: 0.5rem;
            }
        }
    }
`;

export const StyledSidebarSubsection = styled.div`
    font-family: 'Open Sans';
    font-size: 0.75rem;

    .subsection-accordion {
        ${accordionStyles};

        &-summary {
            padding: 0rem 1rem;
            margin-left: -1rem;
            /* height: 1.5rem; */
            font-weight: 500;
            cursor: pointer;
            width: fit-content;
            column-gap: 0.25rem;
            border-radius: 0.25rem;

            span {
                margin: 0;
            }

            &:hover {
                text-decoration: underline;
            }

            &.active {
                background-color: ${colors.steel50};
                text-decoration: underline;
            }
        }

        &-content {
            margin-top: 0.5rem;
            margin-left: 0.5rem;
            padding-left: 1rem;
            border-left: 1px solid ${colors.steel100};

            .submenu {
                flex: 1;
                display: flex;
                flex-flow: column nowrap;
                justify-content: flex-start;
                align-items: stretch;
                row-gap: 0.5rem;

                &-entry {
                    display: flex;
                    flex-flow: column nowrap;
                    justify-content: flex-start;
                    align-items: flex-start;
                    row-gap: 0.5rem;

                    &-title {
                        font-weight: 600;
                        color: ${colors.steel300};
                    }

                    &-option {
                        position: relative;
                        font-weight: 400;
                        cursor: pointer;

                        &.active {
                            font-weight: 500;
                            text-decoration: underline;

                            &::before {
                                background-color: ${colors.steel600};
                            }
                        }

                        &::before {
                            content: '';
                            position: absolute;
                            top: 0;
                            left: calc(-1rem - 0.125rem);
                            width: 0.25rem;
                            height: 100%;
                            border-radius: 0.25rem;
                            background-color: transparent;
                            transition: 250ms background-color ease-in-out;
                        }

                        &:hover {
                            text-decoration: underline;

                            &::before {
                                background-color: ${colors.steel600};
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const StyledSidebarUserCard = styled.div`
    display: flex;
    flex-flow: column nowrap;
    justify-content: flex-start;
    align-items: stretch;
    row-gap: 0.5rem;
    padding: 0.5rem;
    background-color: ${themeBase.white};
    font-family: 'Open Sans';
    font-size: 0.75rem;
    border: 1px solid ${colors.steel100};
    border-radius: 0.5rem;
    max-height: unset;

    .title {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-items: flex-start;

        .info {
            flex: 1;
            display: flex;
            flex-flow: row nowrap;
            column-gap: 0.5rem;

            .text {
                display: flex;
                flex-flow: column nowrap;
                justify-content: space-between;
                font-weight: 500;

                .name {
                    font-family: 'Poppins', sans-serif;
                }

                .type {
                    color: ${colors.steel700};
                }
            }
        }
    }

    .actions {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: center;

        span {
            font-weight: ${fontWeightMedium};
        }

        path {
            fill: ${colors.steel300} !important;
            stroke: ${colors.steel300} !important;
        }

        .logout {
            text-transform: none;
        }
    }
`;

export const StyledSidebarTooltip = styled(Tooltip as any)`
    div.MuiTooltip-tooltip {
        margin-left: 2.375rem !important;
        margin-bottom: -1rem !important;
    }

    .efz-tooltip-content {
        padding-block: 0.5rem !important;
    }
`;

export const LanguageSelectorDialogContent = styled.div`
    display: flex;
    flex-flow: column;

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        row-gap: 0.5rem;

        li {
            width: 100%;
            padding: 0.625rem 1.5rem;
            background-color: ${colors.steel50};
            border-radius: 5px;
            font-weight: 500;
            font-size: 14px;
            border: 1.5px solid transparent;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            &:hover {
                background-color: ${themeBase.white};
                border: 1.5px solid ${colors.steel500};
            }
        }
    }
`;
